import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import "./signup-labels.css";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import { changeTitlePageByResource } from "../../../helpers/helpers";
import { useSecretManager } from "../../../hooks/useSecretManager";
import { CompanyProfile } from "../../../models/company.profile";
import { Error } from "../../../models/error";
import CompanyService, { SignUp } from "../../../services/company.service";
import ProfileCompanyService from "../../../services/profile.company.service";
import { CodeError, CompanyProfileInitial, Step, isStep } from "../../../util/enums";

import env from "../../../util/env";

import FirstStep, { FirstStepHandle } from "./components/FirstStep";
import SecondStep, { SecondStepHandle } from "./components/SecondStep";
import ThirdStep, { ThirdStepHandle } from "./components/ThirdStep";
import FourthStep, { FourthStepHandle } from "./components/FourthStep";
import FifthStep from "./components/FifthStep";
import SixthStep from "./components/SixthStep";

import LeftSideWrapper from "./components/LeftSideWrapper";
import RightSideWrapper from "./components/RightSideWrapper";
import { GridItemForm, GridItemInfo, MainStack } from "./styles";

import Image03 from "../../../assets/img/Image03.png";

const SignUpPage = () => {
  const firstStepRef = useRef<FirstStepHandle>(null);
  const secondStepRef = useRef<SecondStepHandle>(null);
  const thirdStepRef = useRef<ThirdStepHandle>(null);
  const fourthStepRef = useRef<FourthStepHandle>(null);

  const [profiles, setProfiles] = useState<CompanyProfile[]>([]);
  const [actualStep, setActualStep] = useState<number>(Step.FIRST);
  const [objectPreview, setObjectPreview] = useState<any>({});
  const [theAccountWasCreated, setTheAccountWasCreated] = useState<boolean>(false);

  const { t: translate } = useTranslation();
  const { secret } = useSecretManager(env.SECRET_ID);

  const safeStep = isStep(actualStep) ? actualStep : Step.FIRST;

  useQuery(
    ["profile.company.get.all"],
    () =>
      ProfileCompanyService.getAll({
        sortBy: JSON.stringify([["id", "asc"]]),
      }),
    {
      onSuccess: (response) => {
        if (response?.data) {
          const ALLOWED = new Set(["Moving", "Supply Chain"]);
          const list = (response.data as any[]).filter((v) => v?.initials && ALLOWED.has(v?.name));
          setProfiles(list || []);
        }
      },
    }
  );

  useEffect(() => {
    changeTitlePageByResource("signup");
  }, []);

  const onSetObjectPreview = () => {
    if (safeStep === Step.FIRST && firstStepRef.current) {
      const { email, firstName, lastName, phone, country, subdomain } = firstStepRef.current;
      setObjectPreview((prev: any) => ({
        ...prev,
        email,
        name: firstName,
        firstName,
        lastName,
        phone,
        country,
        subdomain,
      }));
    } else if (safeStep === Step.SECOND && secondStepRef.current) {
      const { companyName, companyType } = secondStepRef.current;
      setObjectPreview((prev: any) => ({
        ...prev,
        companyName,
        companyInitials: companyType,
      }));
    } else if (safeStep === Step.THIRD && thirdStepRef.current) {
      const { areas } = thirdStepRef.current;
      setObjectPreview((prev: any) => ({ ...prev, areas }));
    } else if (safeStep === Step.FOURTH && fourthStepRef.current) {
      const { videoSurveys, salesReps, storage, warehouses, shipmentTypes, shipmentVolume } =
        fourthStepRef.current as any;
      setObjectPreview((prev: any) => ({
        ...prev,
        videoSurveys,
        salesReps,
        storage,
        warehouses,
        shipmentTypes,
        shipmentVolume,
      }));
    }
  };

  const { mutate: signUp, isLoading } = useMutation(
    ["company.sign.up"],
    ({ signUp }: { signUp: SignUp }) => CompanyService.signUp(signUp),
    {
      onSuccess: () => setTheAccountWasCreated(true),
      onError: (error: AxiosError<Error>) => {
        if (
          error?.response?.data?.field === "subdomain" &&
          error?.response?.data?.error === CodeError.NOT_AVAILABLE
        ) {
          return NotificationToast.error(
            translate("thisSubdomainIsNotAvailablePleaseChooseADifferentOne")
          );
        }

        NotificationToast.error(
          error?.response?.data?.message || translate("thereWasAnErrorCreatingTheAccount")
        );
      },
    }
  );

  const onSubmit = (finalData?: any) => {
    const completeData = finalData ? { ...objectPreview, ...finalData } : objectPreview;

    // clear subdomain if companyInitials is not "MV" of Moving
    if (completeData.companyInitials !== CompanyProfileInitial.MV && completeData.subdomain) {
      completeData.subdomain = "";
    }

    signUp({ signUp: completeData });
  };

  const renderFormByStep = () => {
    if (safeStep === Step.SECOND) {
      return (
        <SecondStep
          ref={secondStepRef}
          profiles={profiles}
          objectPreview={objectPreview}
          handleNextStep={() => {
            onSetObjectPreview();
            setActualStep(Step.THIRD);
          }}
          handleSkipToStep={(step: number) => {
            onSetObjectPreview();
            setActualStep(step);
          }}
          onPreviousStep={() => {
            onSetObjectPreview();
            setActualStep(Step.FIRST);
          }}
          isLoading={isLoading}
        />
      );
    }

    if (safeStep === Step.THIRD) {
      return (
        <ThirdStep
          ref={thirdStepRef}
          objectPreview={objectPreview as any}
          onPreviousStep={() => {
            onSetObjectPreview();
            setActualStep(Step.SECOND);
          }}
          handleNextStep={() => {
            onSetObjectPreview();
            setActualStep(Step.FOURTH);
          }}
          isLoading={isLoading}
        />
      );
    }

    if (safeStep === Step.FOURTH) {
      return (
        <FourthStep
          ref={fourthStepRef}
          objectPreview={objectPreview as any}
          onPreviousStep={() => {
            onSetObjectPreview();
            setActualStep(Step.THIRD);
          }}
          handleNextStep={() => {
            onSetObjectPreview();
            setActualStep(Step.FIFTH);
          }}
          isLoading={isLoading}
        />
      );
    }

    if (safeStep === Step.FIFTH) {
      return (
        <FifthStep
          defaultValue={objectPreview?.discover ?? null}
          companyType={objectPreview?.companyInitials}
          onPreviousStep={() => {
            setActualStep(Step.FOURTH);
          }}
          onBackToStep={(step: number) => {
            setActualStep(step);
          }}
          onNextStep={(data: {
            discover: string;
            otherDetails?: string;
            referralSource?: string;
          }) => {
            setObjectPreview((prev: any) => ({ ...prev, ...data }));
            setActualStep(Step.SIXTH);
          }}
          isLoading={isLoading}
        />
      );
    }

    if (safeStep === Step.SIXTH) {
      return (
        <SixthStep
          objectPreview={objectPreview as any}
          onPreviousStep={() => setActualStep(Step.FIFTH)}
          onSubmit={(finalData: {
            ownWords: string;
            newsLetter: boolean;
            termsAccepted: boolean;
            captchaToken?: string | null;
          }) => {
            setObjectPreview((prev: any) => ({ ...prev, ...finalData }));
            onSubmit(finalData);
          }}
          isLoading={isLoading}
        />
      );
    }

    return (
      <FirstStep
        ref={firstStepRef}
        objectPreview={objectPreview}
        setNextStep={() => {
          onSetObjectPreview();
          setActualStep(Step.SECOND);
        }}
      />
    );
  };

  const useVideoLeft =
    safeStep === Step.FIRST || safeStep === Step.SECOND || safeStep === Step.FIFTH;

  return (
    <MainStack data-cy="signup-main" className="signup-page">
      <Grid container height="100%">
        <GridItemInfo item lg={6} md={12} sx={{ display: { xs: "none", lg: "block" } }}>
          <LeftSideWrapper
            accountHasBeenCreated={theAccountWasCreated}
            variant={useVideoLeft ? "video" : "image"}
            imageSrc={Image03}
          />
        </GridItemInfo>

        <GridItemForm item lg={6} md={12} data-right-panel="1">
          <RightSideWrapper
            accountHasBeenCreated={theAccountWasCreated}
            actualStep={safeStep}
            formByStep={renderFormByStep()}
            centerContent={true}
            isLoading={isLoading}
          />
        </GridItemForm>
      </Grid>
    </MainStack>
  );
};

export default SignUpPage;
